import { createFileRoute, Link } from "@tanstack/react-router";
import { Building2, BadgeCheck, Search, Sparkles, ShieldCheck, ArrowRight } from "lucide-react";
import { useLocale } from "@/lib/i18n/locale-context";
import { getPageCopy } from "@/lib/i18n/page-copy";
import { localizedPath } from "@/lib/i18n/slug-map";

export const Route = createFileRoute("/foretag/")({
  head: () => {
    const title = "Sälj som företag på Sellavo – gratis företagsprofil";
    const description =
      "Skapa en företagsprofil på Sellavo, visa dina villkor och sälj till köpare i hela Sverige. Verifierat organisationsnummer och egen sida med alla dina annonser.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://sellavo.com/foretag" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: "https://sellavo.com/foretag" }],
    };
  },
  component: BusinessLanding,
});

const STEPS = [
  {
    icon: Building2,
    title: "Fyll i organisationsnumret",
    text: "Vi hämtar företagsnamn, säte och skatteuppgifter automatiskt så att du slipper skriva in dem.",
  },
  {
    icon: BadgeCheck,
    title: "Bli verifierad",
    text: "Uppgifterna kontrolleras mot Bolagsverket. Verifierade företag får en tydlig märkning i alla annonser.",
  },
  {
    icon: Sparkles,
    title: "Publicera med AI",
    text: "Fota prylen – AI:n skriver rubrik, beskrivning och föreslår pris. Du godkänner och publicerar.",
  },
];

export function BusinessLanding() {
  const { lang } = useLocale();
  const copy = lang === "sv" ? null : getPageCopy(lang).business;
  const steps = copy ? STEPS.map((step, index) => ({ ...step, title: copy.steps[index]?.[0] ?? step.title, text: copy.steps[index]?.[1] ?? step.text })) : STEPS;
  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <section className="rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-card to-card p-8 sm:p-12">
        <p className="text-sm font-semibold uppercase tracking-wide text-primary">{copy?.eyebrow ?? "För företag"}</p>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
          {copy?.title ?? "Sälj som företag på Sellavo"}
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          {copy?.intro ?? "Egen företagssida med dina villkor, verifierat organisationsnummer och alla annonser samlade. Köpare ser direkt att de handlar av ett företag – med ångerrätt och reklamationsrätt."}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to={localizedPath("/konto", lang)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
          >
            {copy?.create ?? "Skapa företagsprofil"} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/foretag/lista"
            className="inline-flex items-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-semibold transition hover:bg-muted"
          >
            <Search className="h-4 w-4" /> {copy?.browse ?? "Se företag som säljer"}
          </Link>
        </div>
      </section>

      <section className="mt-10 grid gap-4 sm:grid-cols-3">
        {steps.map((s) => (
          <article key={s.title} className="rounded-xl border border-border bg-card p-5">
            <s.icon className="h-6 w-6 text-primary" />
            <h2 className="mt-3 font-semibold">{s.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
          </article>
        ))}
      </section>

      <section className="mt-10 rounded-xl border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold">
          <ShieldCheck className="h-5 w-5 text-primary" /> {copy?.safety ?? "Trygghet som köparen ser"}
        </h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          {(copy?.bullets ?? ["Ångerrätt, garanti och returvillkor visas på varje annons.", "Verifierade uppgifter från Bolagsverket, inte bara det ni skriver själva.", "Säker affär med betalning som hålls kvar tills köparen godkänt varan."]).map((item) => <li key={item}>· {item}</li>)}
        </ul>
      </section>
    </main>
  );
}
